import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useFetchData } from '@/api/apiHooks';
import {
  clearConnectRedirectLocation,
  clearReconnectConnectionId,
  CONNECTION_ID_PARAM,
  CONNECTION_PROVIDER_PARAM,
  getConnectionId,
  getConnectionProvider,
  saveConnectRedirectLocation,
} from '@/utils/marketingConnectRedirect';
import {
  CompaniesResponseCompany,
  PanelsResponsePanel,
  RecruitmentMarketingProvider,
} from '@/utils/types';
import { URLS } from '@/utils/urls';

import {
  ConnectionStatus,
  ConnectionStatusCard,
  isActiveConnectionStatus,
} from './ConnectionStatusCard';
import { useProviderSettingsModal } from './ProviderSettingsModalContext';
import { RECRUITMENT_MARKETING_PROVIDER_DETAILS } from './recruitmentMarketingProviders';
import {
  mapConnectionStatus,
  useDeleteProviderAccount,
  useGenerateAuthUrl,
  useProviderReconnect,
} from './useRecruitmentMarketingConnection';

export interface ProviderConnectionCardProps {
  provider: RecruitmentMarketingProvider;
  panelId: string;
  companyId: string;
  onStatusChange?: (status: ConnectionStatus) => void;
}

export const ProviderConnectionCard = ({
  provider,
  panelId,
  companyId,
  onStatusChange,
}: ProviderConnectionCardProps) => {
  const { label, shortName, logo, scope } =
    RECRUITMENT_MARKETING_PROVIDER_DETAILS[provider];
  const isPanelScoped = scope === 'panel';
  const queryClient = useQueryClient();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  // Only the provider that actually came back off the OAuth redirect reacts to
  // it — both cards are mounted side by side on the integrations list.
  const [isReturningFromRedirect] = useState(
    () =>
      !!getConnectionId(location.search) &&
      getConnectionProvider(location.search) === provider
  );
  const [initialReturningConnectionId] = useState(() =>
    isReturningFromRedirect ? getConnectionId(location.search) : ''
  );
  const { openModal, closeModal } = useProviderSettingsModal();
  const { data: companyData } = useFetchData<CompaniesResponseCompany>({
    url: URLS.getCompanyById(companyId),
    enabled: !!companyId && !isPanelScoped,
  });

  const { data: panelData } = useFetchData<PanelsResponsePanel>({
    url: URLS.getPanel(panelId),
    enabled: !!panelId,
  });
  const connection = panelData?.recruitment_marketing_connections?.find(
    (panelConnection) => panelConnection.provider === provider
  );
  const connectionStatus = mapConnectionStatus(connection?.status);
  // isReturningFromRedirect guarantees the connection exists once panelData loads, since the callback only redirects back on success.
  // 'error' and 'expired' are terminal, so stop waiting and let the card surface them.
  const isFinalizingConnection =
    isReturningFromRedirect &&
    !isActiveConnectionStatus(connectionStatus) &&
    connectionStatus !== 'error' &&
    connectionStatus !== 'expired';
  const { mutate: generateAuthUrl, isPending: isConnecting } =
    useGenerateAuthUrl(provider);
  const { reconnect, isReconnecting } = useProviderReconnect(
    provider,
    companyId,
    panelId
  );
  const { mutate: deleteAccount, isPending: isDisconnecting } =
    useDeleteProviderAccount(provider, connection?.connection_id ?? '');
  const [isRedirectingToProvider, setIsRedirectingToProvider] = useState(false);

  const handleViewSettings = () => {
    if (!connection?.connection_id) return;
    openModal({
      provider,
      connectionId: connection.connection_id,
      mode: 'settings',
    });
  };

  const handleConnect = () => {
    setIsRedirectingToProvider(true);
    // This is a fresh connect: drop any reconnect id a previous attempt left
    // behind, or the callback would PUT /reconnect against a stale connection.
    clearReconnectConnectionId();
    saveConnectRedirectLocation(`${location.pathname}${location.search}`);

    generateAuthUrl(
      { company_id: companyId, panel_id: panelId },
      {
        onSuccess: (response) => {
          window.location.href = response.data.auth_url;
        },
        onError: () => {
          clearConnectRedirectLocation();
          setIsRedirectingToProvider(false);
        },
      }
    );
  };

  const handleReconnect = () => {
    setIsRedirectingToProvider(true);
    reconnect(connection?.connection_id ?? '', {
      onError: () => setIsRedirectingToProvider(false),
    });
  };

  const handleDisconnect = () => {
    deleteAccount(undefined, {
      onSuccess: () => {
        closeModal();
        queryClient.invalidateQueries({ queryKey: [URLS.getPanel(panelId)] });
        // Disconnecting drops the panel's campaigns tracked by this provider,
        // so the table below this card is stale until its query refetches too.
        queryClient.invalidateQueries({
          queryKey: [URLS.getMarketingCampaigns(panelId)],
        });
      },
      onError: () => {},
    });
  };

  useEffect(() => {
    if (isReturningFromRedirect && initialReturningConnectionId) {
      openModal({
        provider,
        connectionId: initialReturningConnectionId,
        mode: 'confirm',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onStatusChange?.(connectionStatus);
  }, [connectionStatus, onStatusChange]);

  useEffect(() => {
    if (!getConnectionId(location.search)) return;
    // Both cards render this effect, so whichever runs first strips the pair for
    // everyone — the state that depends on it was already captured on mount.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CONNECTION_ID_PARAM);
        next.delete(CONNECTION_PROVIDER_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [location.search, setSearchParams]);

  return (
    <ConnectionStatusCard
      icon={<img src={logo} alt={`${label} Logo`} className="h-5 w-5" />}
      title={label}
      providerName={shortName}
      scope={scope}
      scopeName={isPanelScoped ? panelData?.internal_name : companyData?.name}
      status={connectionStatus}
      expiresAt={connection?.expires_at}
      description={
        connectionStatus === 'error' || connectionStatus === 'expired'
          ? connection?.error_details ?? undefined
          : undefined
      }
      onConnect={handleConnect}
      onReconnect={handleReconnect}
      onDisconnect={handleDisconnect}
      onViewInfo={handleViewSettings}
      isActionDisabled={
        isConnecting || isReconnecting || isRedirectingToProvider
      }
      isActionLoading={
        isConnecting ||
        isReconnecting ||
        isRedirectingToProvider ||
        isDisconnecting
      }
      isLoading={isFinalizingConnection || isDisconnecting}
    />
  );
};
